import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { jwtDecode } from "jwt-decode";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { LocalNotifications } from "@capacitor/local-notifications";
import {
  login as authLogin,
  googleLogin as authGoogleLogin,
  logout as authLogout,
  register as authRegister,
} from "../api/auth";
import api from "../api/api";
import {
  ATTENDANCE_SESSION_KEYS,
  attendanceSessionFromClaims,
} from "../utils/attendanceSession";
import type { WorkspaceSubscription } from "../utils/limitedAccess";
import { clearForeignNamespaces, clearNamespace } from "../utils/cache/httpCache";
import { getCacheNamespace } from "../utils/cache/namespace";
import {
  clearDraftsForNamespace,
  clearForeignDraftNamespaces,
} from "../utils/chat/draftStorage";

type UserType = {
  id: string;
  name: string;
  email: string;
  role:
    | "superadmin"
    | "admin"
    | "hr head"
    | "manager"
    | "employee"
    | "supervisor"
    | "director"
    | "founder";
  managerID?: string;
  avatar_url?: string | null;
  subscription?: WorkspaceSubscription;
};

type AuthContextType = {
  isAuthenticated: boolean;
  user: UserType | null;
  login: (email: string, password: string, head_office_id: string) => Promise<UserType>;
  googleLogin: (ticket: string, head_office_id: string) => Promise<UserType>;
  register: (email: string, password: string, role: string, first_name: string, last_name: string, mobile: string, company_name?: string, google_ticket?: string) => Promise<void>;
  logout: () => Promise<void>;
  clockOut: () => void;
  /** Update the signed-in user's avatar app-wide after upload/remove. */
  updateAvatar: (url: string | null) => void;
};

const isNative = () => Capacitor.isNativePlatform();

const saveToStorage = async (key: string, value: string) => {
  if (isNative()) {
    await Preferences.set({ key, value });
  } else {
    localStorage.setItem(key, value);
  }
};

const getFromStorage = async (key: string): Promise<string | null> => {
  if (isNative()) {
    const { value } = await Preferences.get({ key });
    return value;
  } else {
    return localStorage.getItem(key);
  }
};

const removeFromStorage = async (key: string) => {
  if (isNative()) {
    await Preferences.remove({ key });
  } else {
    localStorage.removeItem(key);
  }
};

const STORAGE_KEYS = [
  "accessToken", "refreshToken", "user", "level", "employee_id",
  "branchID", "headOfficeID", "masterID", "fcmtoken", "workfromhome",
  "workingHoursStart1", "workingHoursEnd1", "maxDistance1", "officeLon1", "officeLat1",
  "workingHoursStart2", "workingHoursEnd2", "maxDistance2", "officeLon2", "officeLat2",
  "workingHoursStart3", "workingHoursEnd3", "maxDistance3", "officeLon3", "officeLat3",
];

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = await getFromStorage("accessToken");
        const userStr = await getFromStorage("user");
        if (token && userStr) {
          setUser(JSON.parse(userStr));
          setIsAuthenticated(true);
        }
      } catch {
        await clearAllStorage();
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    let timerId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const scheduleRefresh = async () => {
      if (cancelled) return;
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }

      const token = await getFromStorage("accessToken");
      if (!token || cancelled) return;

      let msUntilExpiry = 55 * 60 * 1000;
      try {
        const { exp } = jwtDecode<{ exp: number }>(token);
        if (exp) {
          msUntilExpiry = Math.max(exp * 1000 - Date.now() - 5 * 60 * 1000, 10_000);
        }
      } catch { /* keep fallback */ }

      timerId = setTimeout(async () => {
        if (cancelled) return;
        try {
          const refreshToken = await getFromStorage("refreshToken");
          const currentToken = await getFromStorage("accessToken");
          const response = await api.post(
            "/api/auth/refresh",
            refreshToken ? { refresh_token: refreshToken } : {},
            { headers: { Authorization: `Bearer ${currentToken}` } }
          );
          const newToken = response.data?.access_token ?? response.data?.accessToken;
          if (newToken && !cancelled) {
            await saveToStorage("accessToken", newToken);
            if (response.data?.refresh_token) {
              await saveToStorage("refreshToken", response.data.refresh_token);
            }
          }
          if (!cancelled) scheduleRefresh();
        } catch {
          // 401 interceptor in api.ts handles forced logout on next request
        }
      }, msUntilExpiry);
    };

    scheduleRefresh();

    return () => {
      cancelled = true;
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }
    };
  }, [isAuthenticated]);

  const clearAllStorage = async () => {
    for (const key of STORAGE_KEYS) {
      await removeFromStorage(key);
    }
  };

  // Everything a completed sign-in has to do once a token exists — cache
  // namespace, attendance claims, identity hydration. Shared by the password
  // flow and the Google flow, which differ only in how identity is proved.
  const adoptSession = async (result: Awaited<ReturnType<typeof authLogin>>) => {
    if (result?.access_token) {
      await saveToStorage("accessToken", result.access_token);
      if (result.refresh_token) {
        await saveToStorage("refreshToken", result.refresh_token);
      }
      // A different user on this machine must not inherit the previous
      // login's cached pages.
      await clearForeignNamespaces(getCacheNamespace());
      // Unsent chat drafts are message content and live outside that cache,
      // so they need the same sweep — nobody should find the last user's
      // half-written messages waiting for them on a shared machine.
      clearForeignDraftNamespaces(getCacheNamespace());
      const tokenClaims = jwtDecode(result.access_token) as any;
      for (const key of ATTENDANCE_SESSION_KEYS) {
        await removeFromStorage(key);
      }
      const attendanceFields = attendanceSessionFromClaims(tokenClaims);
      for (const [key, value] of Object.entries(attendanceFields)) {
        await saveToStorage(key, value);
      }
      // The HRMS JWT is the subscription authority. Some login responses also
      // include a smaller `user` object; merge it over the claims without
      // dropping the subscription entitlement carried by the token.
      const decodedUser = {
        ...(tokenClaims as UserType),
        ...((result.user as UserType | undefined) || {}),
        subscription:
          (tokenClaims as UserType).subscription ??
          (result.user as UserType | undefined)?.subscription,
      } as UserType;
      let hydratedUser = decodedUser;
      if (decodedUser?.id) {
        try {
          const identityResp = await api.get(`/identity/users/${decodedUser.id}`, {
            headers: { Authorization: `Bearer ${result.access_token}` },
          });
          hydratedUser = {
            ...decodedUser,
            avatar_url: identityResp.data?.avatar_url ?? null,
          };
        } catch {
          hydratedUser = { ...decodedUser, avatar_url: null };
        }
      }
      await saveToStorage("user", JSON.stringify(hydratedUser));
      setUser(hydratedUser);
      setIsAuthenticated(true);
      return hydratedUser;
    } else {
      throw new Error("Login failed");
    }
  };

  const login = async (email: string, password: string, head_office_id: string) => {
    try {
      return await adoptSession(await authLogin({ email, password, head_office_id }));
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const googleLogin = async (ticket: string, head_office_id: string) => {
    try {
      return await adoptSession(await authGoogleLogin(ticket, head_office_id));
    } catch (error) {
      console.error("Google login error:", error);
      throw error;
    }
  };

  const register = async (
    email: string,
    password: string,
    role: string,
    first_name: string,
    last_name: string,
    mobile: string,
    company_name?: string,
    google_ticket?: string,
  ) => {
    // Delegate to the real /api/auth/register endpoint defined in api/auth.ts.
    // Returns the created user payload; we don't auto-login the caller, so the
    // Register page can decide what to do next (currently it navigates to /login).
    await authRegister(email, password, role, first_name, last_name, mobile, company_name, google_ticket);
  };

  const clockOut = async () => {
    const { keys } = await Preferences.keys();
    const allData: Record<string, any> = {};
    for (const key of keys) {
      const { value } = await Preferences.get({ key });
      allData[key] = value;
    }
    const res = await api.post("/attendance", { ...allData, clockIn: false });
    // Leaving before the shift ends can be approval-based (policy rule
    // attendance.early_exit_approval). The punch is REFUSED with a 200 and a
    // status, so a plain status-code check would cheerfully announce a
    // clock-out that never happened — and the employee would find out at
    // payroll. Same reason useAttendanceClock throws on this.
    const approvalRequired = res.data?.result?.status === "approval_required";
    const ok = res.status === 200 && !approvalRequired;
    await LocalNotifications.schedule({
      notifications: [{
        title: approvalRequired
          ? "Approval needed ⏳"
          : ok ? "Attendance Marked ✅" : "Attendance Marked ❌",
        body: approvalRequired
          ? (res.data?.result?.message ||
             "Leaving early needs approval. Open Attendance to send the request.")
          : ok ? "You have successfully clocked out"
               : "You have not clocked out yet, Please try again",
        id: 1,
        schedule: { at: new Date(Date.now() + 1000) },
      }],
    });
  };

  const logout = async () => {
    // Capture the namespace BEFORE authLogout() deletes the access token it is
    // derived from — otherwise the wipe targets 'anon' and the user's cached
    // data survives the logout.
    const ns = getCacheNamespace();
    // authLogout() posts to /api/auth/logout to release this device's
    // single-session slot BEFORE it clears the stored token — the endpoint
    // needs that token to know whose slot to free. It must be awaited (not
    // fire-and-forget) so that ordering actually happens before the
    // clearAllStorage()/clearNamespace() calls below run. This exact
    // ordering bug has already shipped once here (logout used to only clear
    // storage locally and never called the backend at all) — do not reorder
    // this a third time.
    await authLogout();
    await clearAllStorage();
    await clearNamespace(ns);
    clearDraftsForNamespace(ns);
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateAvatar = useCallback((url: string | null) => {
    setUser((prev) => {
      if (!prev) return prev;
      const next = { ...prev, avatar_url: url };
      void saveToStorage("user", JSON.stringify(next));
      return next;
    });
  }, []);

  if (loading) return <div>Loading...</div>;

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, googleLogin, register, logout, clockOut, updateAvatar }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
